import { IShareContentRecipient } from '@tableau/share-dialog';
import _ from 'underscore';

import { Set } from '~src/commonInfra/utils/Set';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { ITypedIdWithName } from '~src/httpClient/serverApi/types/ITypedId';
import { ExplorableItemType, ExplorableItemTypeValue } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { ISiteUser } from '~src/userManagement/users/serverApi/types/ISiteUser';
import { IUser } from '~src/userManagement/users/serverApi/types/IUser';

import { EffectivePermissions } from '../requests/EffectivePermissions';
import { ExplicitPermissionsResult, getExplicitPermissionsForAuthorizables } from '../requests/ExplicitPermissions';
import { GetActionableObjectsForAction } from '../requests/GetActionableObjectsForAction';
import { AuthorizableType, getAuthorizableType } from '../types/AuthorizableType';
import { Capabilities } from '../types/Capabilities';
import { AI_ACCESS_CAPABILITY_SCOPE } from '../types/CapabilityScope';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { IPermission, Permission } from '../types/Permission';
import { PermissionsEditabilityLogic } from './PermissionsEditabilityLogic';

export module GrantAccessPermissionsHelper {
  type CanGrantPermissionType = {
    canGrantPermissions: boolean;
    /* only included if the content does not control its own permissions */
    sourceAuthorizable?: ITypedIdWithName;
  }

  export async function getUsersWithoutReadPermissionsForContent(shareRecipients: IShareContentRecipient[], content: IContentInfo<ExplorableItemType>) {
    return getUsersWithoutCapabilitiesForContent(shareRecipients, content, [Capabilities.read]);
  }

  // AI Access requires BOTH Full Data Query (`connect`) and AI Access (`ai_access`) — AI features
  // query the workbook's data, so `ai_access` alone is not enough. Derive the required set from the
  // same AI_ACCESS_CAPABILITY_SCOPE used to build the grant template, so the two stay in sync. A user
  // missing EITHER capability is treated as not yet having AI access and is included in the grant
  // flow, which then grants the full set.
  export async function getUsersWithoutAiAccessPermissionsForContent(shareRecipients: IShareContentRecipient[], content: IContentInfo<ExplorableItemType>) {
    // `connect` and `ai_access` are capabilities of a workbook, not of a project. For a project the
    // grant fans out to every workbook through the project's Workbooks permission template, so the
    // recipient's effective capabilities live under the `projectWorkbookTemplate` bucket rather than
    // the bare `project` bucket (which only carries read/write). The effective-permissions endpoint
    // returns that template bucket alongside the project when queried with {type: 'project'} -- the
    // same expansion the permissions dialog's Workbooks tab relies on -- so we keep the request keyed
    // to the content type and only read from the template bucket. Reading the `project` bucket would
    // find neither capability and wrongly treat every recipient as missing AI access. Workbook/view
    // carry `connect`/`ai_access` directly, so no remap is needed there.
    const permissionsBucketType: string = content.contentType === ExplorableItemTypeValue.project
      ? AuthorizableType.PROJECT_WORKBOOK_TEMPLATE
      : content.contentType;
    return getUsersWithoutCapabilitiesForContent(shareRecipients, content, AI_ACCESS_CAPABILITY_SCOPE, permissionsBucketType);
  }

  async function getUsersWithoutCapabilitiesForContent(shareRecipients: IShareContentRecipient[], content: IContentInfo<ExplorableItemType>, capabilities: ReadonlyArray<string>, permissionsBucketType: string = content.contentType) {
    const userIds: string[] = shareRecipients.map(recipient => recipient.recipientId.toString());
    const effectivePermissions: ListResult<IGranteePermissions> = await EffectivePermissions.getEffectivePermissionsWithoutGettingSiteUsers(
      userIds,
      { type: content.contentType, ids: [content.id] }
    );
    const usersWithoutPermissions: (IUser | ISiteUser)[] = [];
    for (let perms of effectivePermissions.items) {
      const permissionsForContent: _.Dictionary<IPermission> = perms.permissionsByContentType[permissionsBucketType];
      // A user is "without" the access unless EVERY required capability is explicitly Allowed.
      const hasAllCapabilities = capabilities.every(
        (capability) => permissionsForContent?.[capability]?.permission === Permission.Allowed
      );
      if (!hasAllCapabilities) {
        usersWithoutPermissions.push(perms.user);
      }
    }
    return usersWithoutPermissions;
  }

  export async function canUserGrantPermission(content: IContentInfo<ExplorableItemType>): Promise<CanGrantPermissionType> {
    const authorizableType = getAuthorizableType(content.contentType);
    const explicitPermissions: ExplicitPermissionsResult = await getExplicitPermissionsForAuthorizables({type: authorizableType, ids: [content.id]}, false);
    if (explicitPermissions.errors && explicitPermissions.errors.length) {
      // if the user cannot get explicit permissions, then they are not allowed to set permissions
      return {canGrantPermissions: false};
    }
    const authorizable = Set.fromIdItemArray([content]);
    const isEditable: boolean = PermissionsEditabilityLogic.getPermissionsEditability(authorizable, authorizableType, explicitPermissions.sourceAuthorizablesById).editable;
    if (isEditable) {
      // if the user can get explicit permissions AND the permissions are editable, it means that they can set permissions
      return {canGrantPermissions: true};
    }
    const controllingContent: ITypedIdWithName = explicitPermissions.sourceAuthorizablesById[content.id];
    const actionableItems = await GetActionableObjectsForAction.editPermissions({type: controllingContent.type, ids: [controllingContent.id]});
    if (actionableItems.ids.includes(controllingContent.id)) {
      return {canGrantPermissions: true, sourceAuthorizable: controllingContent};
    }
    return {canGrantPermissions: false};
  }
}
